// ─── Blog Types ────────────────────────────────────────────────────────────

export interface BlogPostMeta {
  slug: string;
  title: string;
  description: string;
  author: string;
  authorTitle: string;
  date: string;            // ISO date string YYYY-MM-DD
  updatedAt?: string;      // ISO date string for GEO freshness tracking
  category: BlogCategory;
  tags: string[];
  primaryKeyword: string;
  secondaryKeywords?: string[];
  readingTime: number;     // minutes
  lang: 'no' | 'en';
  image?: string;          // relative path or URL for og:image
  imageAlt?: string;
  featured?: boolean;
  faq?: BlogFAQ[];         // FAQ section for JSON-LD schema
  translationSlug?: string; // slug of the equivalent post in the other language
}

export interface BlogInteractiveComponent {
  type: string;            // process-animation | step-revealer | concept-revealer | scale-animator | wonder-ballot
  title: string;
  description: string;
  trigger?: string;
  ageRange?: string;
  outputFile?: string;     // path to animation HTML file
}

export interface BlogPost extends BlogPostMeta {
  content: string;         // raw markdown body (no frontmatter)
  interactiveComponent?: BlogInteractiveComponent;
}

export interface BlogFAQ {
  question: string;
  answer: string;
}

export type BlogCategory =
  | 'realfag'              // Realfag for Barn — Science, math, nature
  | 'kreativ-laering'      // Kreativ Læring — Art+STEM fusion
  | 'digital-foreldre'     // Digital Foreldre — Screen time, app guides
  | 'sesong-steam'         // Sesong-STEAM — Seasonal activities
  | 'laereplanen'          // Læreplanen (LK20) — Curriculum alignment
  | 'undring';             // Undring — Wonder/curiosity prompts

// Maps blog categories to mascot subjects for visual treatment
export const CATEGORY_MASCOT: Record<BlogCategory, 'mathematics' | 'science' | 'technology' | 'engineering' | 'arts' | 'nature'> = {
  realfag: 'science',
  'kreativ-laering': 'arts',
  'digital-foreldre': 'technology',
  'sesong-steam': 'nature',
  laereplanen: 'engineering',
  undring: 'science',
};

export const CATEGORY_CONFIG: Record<BlogCategory, {
  labelNo: string;
  labelEn: string;
  color: string;
  icon: string;
}> = {
  realfag: {
    labelNo: 'Realfag for barn',
    labelEn: 'STEM for Kids',
    color: '#16A34A',    // science green
    icon: '🔬',
  },
  'kreativ-laering': {
    labelNo: 'Kreativ læring',
    labelEn: 'Creative Learning',
    color: '#9333EA',    // arts purple
    icon: '🎨',
  },
  'digital-foreldre': {
    labelNo: 'Digital foreldre',
    labelEn: 'Digital Parenting',
    color: '#2563EB',    // tech blue
    icon: '📱',
  },
  'sesong-steam': {
    labelNo: 'Sesong-STEAM',
    labelEn: 'Seasonal STEAM',
    color: '#F59E0B',    // math amber
    icon: '🌿',
  },
  laereplanen: {
    labelNo: 'Læreplanen',
    labelEn: 'Curriculum',
    color: '#EF4444',    // engineering red
    icon: '📚',
  },
  undring: {
    labelNo: 'Undring',
    labelEn: 'Wonder',
    color: '#00C2A8',    // teal brand
    icon: '✨',
  },
};

// Content calendar entry used by scheduled tasks
export interface ContentCalendarEntry {
  date: string;            // YYYY-MM-DD
  title: string;
  slug: string;
  category: BlogCategory;
  primaryKeyword: string;
  secondaryKeywords: string[];
  status: 'planned' | 'draft' | 'published';
  lang: 'no' | 'en';
}

export interface ContentCalendar {
  month: string;           // YYYY-MM
  generatedAt: string;     // ISO timestamp
  entries: ContentCalendarEntry[];
}
